import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import MentionTextarea from "@/estimating/components/MentionTextarea";
import { clearMentions, unreadFor, useMentions } from "@/estimating/lib/mentions";

// THE app-wide notes cell (see /app/memory/UI_PATTERNS.md › Notes UI). Icon + count; hover reads,
// click reads + adds. Notes are stamped and never edited. @mentions notify through the header bell;
// `target` lights the icon until this row's notes are opened.
const stampOf = (n) => {
  const d = n.at ? new Date(n.at).toLocaleDateString("en-US", { month: "numeric", day: "numeric" }) : "";
  return [n.initials, d].filter(Boolean).join(" ") || "Earlier";
};

const NotesList = ({ notes, testid }) => (
  <ul className="space-y-1.5 text-xs" data-testid={testid}>
    {notes.map((n, i) => (
      <li key={i} className="rounded border border-border p-1.5">
        <div className="text-[10px] text-muted-foreground"><span className="font-medium text-foreground">{stampOf(n)}</span> {n.user}</div>
        <div className="whitespace-pre-wrap text-foreground">{n.text}</div>
      </li>))}
    {!notes.length && <li className="text-[11px] text-muted-foreground">No notes yet.</li>}
  </ul>
);

/** notes: newest first [{text, user, initials, at}]. onAdd(text) → truthy when saved. */
export default function NotesThread({ notes = [], onAdd, testid, title, target, canAdd = true }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { items } = useMentions();
  const lit = target ? unreadFor(items, target) > 0 : false;
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { if (await onAdd(text.trim())) setText(""); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && target) clearMentions(target); }}>
      <HoverCard openDelay={150} closeDelay={50}>
        <HoverCardTrigger asChild>
          <PopoverTrigger asChild>
            <button type="button" title={lit ? "You were mentioned — open to read" : "Notes"} data-testid={testid} data-mention={lit ? "1" : "0"}
                    onClick={(e) => e.stopPropagation()}
                    className={`inline-flex items-center gap-0.5 rounded-sm p-1 text-[10px] ${lit ? "mention-lit" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
              <MessageSquare className="h-3 w-3" />
              <span data-testid={`${testid}-count`}>{notes.length || ""}</span>
            </button>
          </PopoverTrigger>
        </HoverCardTrigger>
        {!open && notes.length > 0 && (
          <HoverCardContent side="left" align="start" className="w-80 max-h-72 overflow-y-auto p-2">
            <NotesList notes={notes} testid={`${testid}-hover`} />
          </HoverCardContent>)}
      </HoverCard>
      <PopoverContent align="end" className="w-[26rem] max-h-[28rem] overflow-y-auto p-3" data-testid={`${testid}-pop`}
                      onClick={(e) => e.stopPropagation()}>
        <div className="pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Notes{title ? ` · ${title}` : ""}</div>
        {canAdd && (<>
          <MentionTextarea autoFocus rows={3} value={text} onChange={setText} onSubmit={submit}
                           placeholder="Add a note — @name to mention someone (stamped, never edited)"
                           className="text-xs" testid={`${testid}-input`} />
          <div className="mt-1 mb-3 flex justify-end">
            <Button size="sm" className="h-7 text-xs" onClick={submit} disabled={busy || !text.trim()} data-testid={`${testid}-submit`}>
              <Send className="mr-1 h-3 w-3" /> Post
            </Button>
          </div>
        </>)}
        <NotesList notes={notes} testid={`${testid}-thread`} />
      </PopoverContent>
    </Popover>
  );
}
